/* eslint-disable max-lines */
import type { ReactElement } from 'react';
import { memo, useCallback, useRef, useState } from 'react';

import type {
    GetStyleIdentifiersQueryVariables,
    StyleAutocompleteOption,
} from '../../../gql';
import type { FilterOption } from '../../../types/FilterOption';

import { useLazyQuery } from '@apollo/client/react';
import { KeyboardKey } from '@bestseller-bit/frontend-community.components.keyboard-key';
import { SearchIcon } from '@bestseller-bit/frontend-community.theme.buying-theme';
import Button from '@mui/material/Button';
import Chip from '@mui/material/Chip';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';

import { useGlobalState } from '../../../../../global_state/src';
import { GetStyleIdentifiersDocument } from '../../../gql';
import { GraphClientNames } from '../../../types/GQLClientNames';
import { isDefined } from '../../../utils/filter-util';

/** Style numbers are always 8 digits — anything else is junk the backend cannot match. */
const styleNumberLength = 8;

interface InsertStylesDialogProps {
    isOpen: boolean;
    onCloseDialog: () => void;
    collectionNumbers: GetStyleIdentifiersQueryVariables['collectionNumbers'];
    /** The styles currently selected in the filter. */
    selectedStyles: Array<FilterOption>;
    /** Called with the merged selection when the user applies the dialog. */
    onStylesChange: (styles: Array<FilterOption>) => void;
    /** Reports validation failures where the caller has telemetry; library stays Datadog-free. */
    onError?: (error: unknown) => void;
}

/** Validates manually entered style numbers and adds matching styles. */
const InsertStylesDialog = memo(
    ({
        isOpen,
        onCloseDialog: handleCloseDialog,
        collectionNumbers,
        selectedStyles,
        onStylesChange: handleStylesChange,
        onError,
    }: InsertStylesDialogProps): ReactElement => {
        const [, dispatchSnackbar] = useGlobalState('snackbarQueue');

        const [input, setInput] = useState('');
        const [styleNumbers, setStyleNumbers] = useState<
            Array<StyleAutocompleteOption['styleNumber']>
        >([]);
        const [validatedStyles, setValidatedStyles] = useState<
            Array<FilterOption>
        >([]);
        const [getStyles] = useLazyQuery(GetStyleIdentifiersDocument);
        const activeRequestIdRef = useRef(0);

        const handleValidateStyles = useCallback((): void => {
            const parsedStyleNumbers = (input.match(/\d+/g) ?? [])
                .filter((digits) => digits.length === styleNumberLength)
                .map((digits) => Number.parseInt(digits, 10));

            if (parsedStyleNumbers.length === 0) return;

            const requestId = ++activeRequestIdRef.current;

            getStyles({
                context: {
                    clientName:
                        GraphClientNames['bestone-bi4-buying-style-bff'],
                },
                variables: {
                    styleNumbers: parsedStyleNumbers,
                    collectionNumbers,
                },
            })
                .then((result) => {
                    if (requestId !== activeRequestIdRef.current) return;

                    const matchingStyles = result.data?.stylesLookup.styles;
                    if (!Array.isArray(matchingStyles)) return;

                    const newValidatedStyles: Array<FilterOption> =
                        matchingStyles.flatMap((style): Array<FilterOption> => {
                            const styleNumber = style?.styleNumber;
                            if (!isDefined(styleNumber)) return [];

                            return [
                                {
                                    label: `${styleNumber} - ${style?.styleName ?? 'Unknown Style Name'}`,
                                    value: String(styleNumber),
                                },
                            ];
                        });

                    setValidatedStyles((currentStyles) => [
                        ...currentStyles,
                        ...newValidatedStyles.filter(
                            (newStyle) =>
                                !currentStyles.some(
                                    (style) => style.value === newStyle.value
                                )
                        ),
                    ]);
                    setStyleNumbers((currentNumbers) => [
                        ...new Set([...currentNumbers, ...parsedStyleNumbers]),
                    ]);
                })
                .catch((stylesError: unknown) => {
                    if (requestId !== activeRequestIdRef.current) return;

                    const message =
                        stylesError instanceof Error
                            ? stylesError.message
                            : 'Unknown error occurred';

                    dispatchSnackbar({
                        type: 'add',
                        payload: {
                            title: 'Something went wrong validating styles',
                            message,
                            type: 'error',
                        },
                    });

                    console.error(stylesError);
                    onError?.(stylesError);
                });

            setInput('');
        }, [collectionNumbers, dispatchSnackbar, getStyles, input, onError]);

        const handleClose = useCallback((): void => {
            activeRequestIdRef.current++;
            setInput('');
            setStyleNumbers([]);
            setValidatedStyles([]);
            handleCloseDialog();
        }, [handleCloseDialog]);

        const handleApplyStyles = useCallback((): void => {
            const selectedValues = new Set(
                selectedStyles.map((style) => style.value)
            );

            handleStylesChange([
                ...selectedStyles,
                ...validatedStyles.filter(
                    (style) => !selectedValues.has(style.value)
                ),
            ]);

            handleClose();
        }, [handleClose, handleStylesChange, selectedStyles, validatedStyles]);

        const handleClearStyles = useCallback((): void => {
            setStyleNumbers([]);
            setValidatedStyles([]);
        }, []);

        const handleRemoveStyle = useCallback(
            (styleNumber: StyleAutocompleteOption['styleNumber']): void => {
                setStyleNumbers((currentNumbers) =>
                    currentNumbers.filter((number) => number !== styleNumber)
                );
                setValidatedStyles((currentStyles) =>
                    currentStyles.filter(
                        (style) => style.value !== String(styleNumber)
                    )
                );
            },
            []
        );

        return (
            <Dialog
                open={isOpen}
                onClose={handleClose}
            >
                <DialogTitle>
                    <Typography variant="headings.h2">
                        Insert style numbers
                    </Typography>
                </DialogTitle>

                <DialogContent>
                    <Stack
                        spacing={3}
                        useFlexGap
                    >
                        <Stack
                            spacing={6}
                            useFlexGap
                        >
                            <Typography
                                variant="body2"
                                component="div"
                            >
                                1. Copy and paste the style numbers in the
                                search area, or write them out separated by
                                space or comma
                                <br />
                                2. Validate and search for styles by clicking
                                <KeyboardKey
                                    sx={{
                                        display: 'inline-flex',
                                        marginInline: '.5em',
                                    }}
                                >
                                    enter
                                </KeyboardKey>
                                or <strong>SEARCH FOR STYLES</strong>
                                <br />
                                3. Confirm styles and apply them to the filter
                                by clicking <strong>APPLY</strong>
                            </Typography>

                            <TextField
                                rows={6}
                                placeholder="e.g. 123456789 987654321"
                                value={input}
                                variant="outlined"
                                multiline
                                fullWidth
                                onKeyDown={(event): void => {
                                    if (
                                        event.key === 'Enter' &&
                                        !event.shiftKey
                                    ) {
                                        event.preventDefault();
                                        handleValidateStyles();
                                    }
                                }}
                                onChange={(event): void => {
                                    setInput(event.target.value);
                                }}
                            />
                        </Stack>

                        <Button
                            size="small"
                            sx={{ alignSelf: 'flex-end' }}
                            endIcon={<SearchIcon />}
                            disabled={input.trim().length === 0}
                            onClick={handleValidateStyles}
                        >
                            Search for styles
                        </Button>

                        {styleNumbers.length > 0 && (
                            <Stack
                                direction="row"
                                spacing={2}
                                sx={{ flexWrap: 'wrap' }}
                                useFlexGap
                            >
                                {styleNumbers.map((styleNumber) => (
                                    <Chip
                                        key={styleNumber}
                                        label={styleNumber}
                                        color={
                                            validatedStyles.some(
                                                (style) =>
                                                    style.value ===
                                                    String(styleNumber)
                                            )
                                                ? 'primary'
                                                : 'error'
                                        }
                                        onDelete={(): void => {
                                            handleRemoveStyle(styleNumber);
                                        }}
                                    />
                                ))}
                            </Stack>
                        )}
                    </Stack>
                </DialogContent>

                <DialogActions
                    sx={{ borderTop: 1, borderColor: 'divider' }}
                    disableSpacing
                >
                    <Stack
                        direction="row"
                        spacing={2}
                        sx={{ flex: 1, justifyContent: 'space-between' }}
                        useFlexGap
                    >
                        <Button
                            variant="text"
                            onClick={handleClose}
                        >
                            Close
                        </Button>

                        <Stack
                            direction="row"
                            spacing={2}
                            useFlexGap
                        >
                            <Button
                                variant="outlined"
                                disabled={styleNumbers.length === 0}
                                onClick={handleClearStyles}
                            >
                                Clear
                            </Button>

                            <Button
                                disabled={validatedStyles.length === 0}
                                onClick={handleApplyStyles}
                            >
                                Apply Styles
                            </Button>
                        </Stack>
                    </Stack>
                </DialogActions>
            </Dialog>
        );
    }
);

InsertStylesDialog.displayName = 'InsertStylesDialog';

export { InsertStylesDialog };
